import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const ShortcutsContext = createContext({ shortcuts: [], byAction: {}, refresh: () => {}, loading: false });

export function ShortcutsProvider({ children }) {
    const [shortcuts, setShortcuts] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/shortcuts");
            setShortcuts(r.data?.shortcuts || []);
        } catch { /* silent — anonymous or offline */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        load();
        const onUpdate = () => load();
        const onFocus = () => load();
        const onStorage = (e) => { if (e.key === "token") load(); };
        window.addEventListener("micm-shortcuts-updated", onUpdate);
        window.addEventListener("focus", onFocus);
        window.addEventListener("storage", onStorage);
        // Retry a few times over the first ~10 s so we catch the moment the
        // user logs in (auth token appears in localStorage) even without
        // a focus event.
        const t1 = setTimeout(load, 2000);
        const t2 = setTimeout(load, 5000);
        const t3 = setTimeout(load, 10000);
        return () => {
            window.removeEventListener("micm-shortcuts-updated", onUpdate);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("storage", onStorage);
            clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
        };
    }, [load]);

    const byAction = useMemo(() => Object.fromEntries(shortcuts.map((s) => [s.action_id, s])), [shortcuts]);
    const value = useMemo(() => ({ shortcuts, byAction, refresh: load, loading }), [shortcuts, byAction, load, loading]);
    return <ShortcutsContext.Provider value={value}>{children}</ShortcutsContext.Provider>;
}

export const useShortcuts = () => useContext(ShortcutsContext);
export const useShortcut = (action_id) => useContext(ShortcutsContext).byAction[action_id];
